import type { ReactNode } from 'react';
import { BrandWordmark } from '../../components/BrandWordmark.tsx';
import { useT } from '../../i18n/index.ts';
import { cx } from '../../ui/index.ts';

/**
 * Public-page frame for the auth suite + invite — Dispatch Deck (epic
 * #897 PR 8). Replaces the console-shared `createAuthPageFrame` factory
 * instantiation: these routes render OUTSIDE the Shell, so the frame
 * owns the full-viewport `canvas` backdrop and centers one raised card.
 *
 * Anatomy: BrandWordmark on top of the card, `title`-tier heading
 * (exactly one `h1` per page — PageHeader is a shell-page recipe and is
 * not used here), dense description, then the page body. The legacy
 * uppercase-tracked "eyebrow" pill idiom dies with the old frame
 * (uppercase+tracking is banned for CJK labels, DESIGN.md §3).
 */

type FrameWidth = 'md' | 'lg';

const WIDTH_CLASSES: Record<FrameWidth, string> = {
  md: 'max-w-[420px]',
  lg: 'max-w-[520px]',
};

interface Props {
  readonly title: string;
  readonly description?: string;
  /** Secondary cross-links rendered under the card (register / forgot…). */
  readonly footer?: ReactNode;
  readonly width?: FrameWidth;
  readonly testId?: string;
  readonly children: ReactNode;
}

export function AuthPageFrame({
  title,
  description,
  footer,
  width = 'md',
  testId,
  children,
}: Props): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6 sm:p-8">
      <main className={cx('flex w-full flex-col gap-4', WIDTH_CLASSES[width])} data-testid={testId}>
        <div className="rounded-xl border border-line bg-card p-6 text-paper shadow-raised sm:p-8">
          <BrandWordmark className="mb-6" descriptor={t('shell.brand.subtitle')} />
          <h1 className="m-0 text-title text-paper">{title}</h1>
          {description !== undefined ? (
            <p className="mb-0 mt-1.5 text-dense text-paper-low">{description}</p>
          ) : null}
          <div className="mt-6">{children}</div>
        </div>
        {footer !== undefined && footer !== null ? (
          <div className="flex flex-col items-start gap-2 px-2">{footer}</div>
        ) : null}
      </main>
    </div>
  );
}

/**
 * Text cross-link inside/under an auth card (「返回登录」,「立即注册」…).
 * Dense tier, warms to `paper` on hover, signal focus ring — the same
 * quiet-link language the Shell uses for secondary navigation.
 */
export const AUTH_LINK_CLASS =
  'rounded text-dense text-paper-low transition-colors duration-120 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40';
